import React, { useState, useEffect, useMemo } from 'react';
import { X, User, ArrowRight, Loader, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import type { EmployeeSelectionView, MovementTypeView, DepartmentSelectionView, LifecycleTemplateView } from '../../../../models/employee';
import { getMovementEmployeeSelections, getMovementDepartments, initiateMovement } from '../../../../api/Employee/internalMovements';
import { getMovementTypes } from '../../../../api/Employee/lifecycleSettings';

interface FormData {
  userId: string;
  movementTypeId: number;
  templateId: number | undefined;
  newPosition: string;
  newDepartmentId: number | undefined;
  effectiveDate: string;
  reason: string;
  /**
   * RECEIVING manager (optional). See InternalMovementInput.managerId.
   *
   * Empty string means "not designated", which the server reads as a deliberate skip of the
   * receiving-manager stage. That is why the form spells the outcome out under the field rather
   * than letting the stage evaporate unannounced.
   */
  managerId: string;
  // Display-only fields
  employeeName: string;
  currentPosition: string;
  currentDepartment: string;
}

interface InitiateMovementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  templates?: LifecycleTemplateView[];
}

const InitiateMovementModal: React.FC<InitiateMovementModalProps> = ({ isOpen, onClose, onSubmit, templates }) => {
  const { t } = useTranslation();
  const currentUserId = useSelector((state: any) => state.user.userId);
  // HR initiations are born Approved server-side (InitiateMovementAsync), so NEITHER manager stage
  // is ever raised for them. The receiving-manager field is still recorded, but promising an
  // approval step that will not happen would be a lie, so the helper copy branches on this.
  const currentUserRole = useSelector((state: any) => state.user.role);
  const initiatorIsHr = currentUserRole === 'Hr';
  const [submitting, setSubmitting] = useState(false);
  const [employees, setEmployees] = useState<EmployeeSelectionView[]>([]);
  const [movementTypes, setMovementTypes] = useState<MovementTypeView[]>([]);
  const [departments, setDepartments] = useState<DepartmentSelectionView[]>([]);
  const [formData, setFormData] = useState<FormData>({
    userId: '',
    movementTypeId: 0,
    templateId: undefined,
    newPosition: '',
    newDepartmentId: undefined,
    effectiveDate: '',
    reason: '',
    managerId: '',
    employeeName: '',
    currentPosition: '',
    currentDepartment: '',
  });

  // Receiving-manager options come from the SAME already-authorised list that feeds the employee
  // picker (InternalMovement/GetEmployeeSelections). That endpoint is company-scoped, excludes
  // archived users and is gated to exactly the authority that may initiate a movement (Hr, or a
  // people-manager by reporting line). No second, wider directory read is introduced, and no
  // endpoint is widened. The only narrowing possible on the client is the one rule that is always
  // true: an employee cannot be their own receiving manager (the server's
  // ResolveReceivingManagerIdAsync rejects that case too, silently, by returning null).
  //
  // The current user is deliberately KEPT in the list, unlike the employee picker: a hiring manager
  // designating themselves is the single most common receiving case.
  const receivingManagerOptions = useMemo(
    () => employees.filter(emp => emp.id !== formData.userId),
    [employees, formData.userId],
  );

  const selectedManagerName = useMemo(
    () => employees.find(emp => emp.id === formData.managerId)?.fullName ?? '',
    [employees, formData.managerId],
  );

  useEffect(() => {
    if (isOpen) {
      // Load employees, movement types, departments
      getMovementEmployeeSelections().then(result => {
        if (!result.errors || result.errors.length === 0) {
          setEmployees(result.payload || []);
        }
      });
      getMovementTypes().then(result => {
        if (!result.errors || result.errors.length === 0) {
          setMovementTypes((result.payload || []).filter(mt => mt.isActive));
        }
      });
      getMovementDepartments().then(result => {
        if (!result.errors || result.errors.length === 0) {
          setDepartments(result.payload || []);
        }
      });
      // Reset form
      const defaultTemplateId = templates?.find(t => t.isDefault)?.id ?? templates?.[0]?.id;
      setFormData({
        userId: '',
        movementTypeId: 0,
        templateId: defaultTemplateId,
        newPosition: '',
        newDepartmentId: undefined,
        effectiveDate: '',
        reason: '',
        managerId: '',
        employeeName: '',
        currentPosition: '',
        currentDepartment: '',
      });
    }
  }, [isOpen, templates]);

  const handleEmployeeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const employee = employees.find(emp => emp.id === e.target.value);
    if (employee) {
      setFormData({
        ...formData,
        userId: employee.id,
        employeeName: employee.fullName,
        currentPosition: employee.title || '',
        currentDepartment: employee.department || '',
        newPosition: employee.title || '',
        newDepartmentId: departments.find(d => d.name === employee.department)?.id,
        // Live rule: the newly chosen employee cannot be their own receiving manager. Changing the
        // employee to whoever was sitting in the manager slot would otherwise leave an invalid pair
        // in state that the server silently downgrades to "no receiving manager".
        managerId: formData.managerId === employee.id ? '' : formData.managerId,
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await initiateMovement({
        userId: formData.userId,
        movementTypeId: formData.movementTypeId,
        templateId: formData.templateId,
        newPosition: formData.newPosition,
        newDepartmentId: formData.newDepartmentId,
        effectiveDate: formData.effectiveDate,
        reason: formData.reason || undefined,
        // Undefined, never '': "not designated" is expressed by omitting the field, matching how
        // reason is sent. (The server treats null and empty alike, but the wire payload should say
        // what it means.)
        managerId: formData.managerId || undefined,
      });
      if (!result.errors || result.errors.length === 0) {
        onSubmit();
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('Initiate Internal Movement')}</h3>
            <p className="text-sm text-gray-600 mt-1">{t('Create promotion, transfer, or contract change')}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Employee Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Select Employee')} <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={formData.userId}
              onChange={handleEmployeeSelect}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">{t('Choose an employee...')}</option>
              {employees.filter(emp => emp.id !== currentUserId).map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.fullName} - {emp.title || ''} ({emp.department || ''})
                </option>
              ))}
            </select>
          </div>

          {/* Movement Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Movement Type')} <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={formData.movementTypeId || ''}
              onChange={(e) => setFormData({ ...formData, movementTypeId: parseInt(e.target.value) || 0 })}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="">{t('Select movement type...')}</option>
              {movementTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          {/* Template (optional) */}
          {templates && templates.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Template')}
              </label>
              <select
                value={formData.templateId || ''}
                onChange={(e) => setFormData({ ...formData, templateId: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">{t('No template')}</option>
                {templates.map(tmpl => (
                  <option key={tmpl.id} value={tmpl.id}>{tmpl.name} {tmpl.isDefault ? t('(Default)') : ''}</option>
                ))}
              </select>
              {formData.templateId && (
                <p className="text-xs text-gray-500 mt-1">
                  {templates.find(t => t.id === formData.templateId)?.description}
                </p>
              )}
            </div>
          )}

          {/* Effective Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Effective Date')} <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              value={formData.effectiveDate}
              onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Before / After Preview */}
          {formData.userId && (
            <div className="grid grid-cols-2 gap-6">
              {/* Before */}
              <div className="bg-muted border border-border rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  {t('Current Details')}
                </h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="text-gray-500">{t('Position')}:</span>
                    <div className="font-medium text-gray-900">{formData.currentPosition || '-'}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('Department')}:</span>
                    <div className="font-medium text-gray-900">{formData.currentDepartment || '-'}</div>
                  </div>
                </div>
              </div>

              {/* After */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  {t('New Details')}
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-blue-800 mb-1">{t('Position')}</label>
                    <input
                      type="text"
                      required
                      value={formData.newPosition}
                      onChange={(e) => setFormData({ ...formData, newPosition: e.target.value })}
                      className="w-full px-3 py-2 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-blue-800 mb-1">{t('Department')}</label>
                    <select
                      value={formData.newDepartmentId || ''}
                      onChange={(e) => setFormData({ ...formData, newDepartmentId: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="w-full px-3 py-2 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">{t('Select department...')}</option>
                      {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>{dept.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Receiving manager — optional by design; the consequence of leaving it empty is
                      stated below the field rather than left to be discovered. */}
                  <div>
                    <label htmlFor="movement-receiving-manager" className="block text-xs font-medium text-blue-800 mb-1">
                      {t('Receiving Manager')}
                    </label>
                    <select
                      id="movement-receiving-manager"
                      value={formData.managerId}
                      onChange={(e) => setFormData({ ...formData, managerId: e.target.value })}
                      aria-describedby="movement-receiving-manager-help movement-receiving-manager-effect"
                      className="w-full px-3 py-2 text-sm border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      <option value="">{t('No receiving manager')}</option>
                      {receivingManagerOptions.map(mgr => (
                        <option key={mgr.id} value={mgr.id}>
                          {mgr.id === currentUserId
                            ? t('{{name}} (you)', { name: mgr.fullName })
                            : mgr.fullName}
                          {mgr.title ? ` - ${mgr.title}` : ''}
                        </option>
                      ))}
                    </select>
                    <p id="movement-receiving-manager-help" className="text-xs text-blue-700 mt-1">
                      {t('The manager gaining the employee. This is not their current manager.')}
                    </p>
                    <p
                      id="movement-receiving-manager-effect"
                      className={`text-xs mt-1 flex items-start ${!initiatorIsHr && !formData.managerId ? 'text-amber-700' : 'text-blue-600'}`}
                    >
                      <Info className="w-3.5 h-3.5 me-1.5 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>
                        {/* Mirrors the server's SELF-APPROVAL RULE in NextStageAfterReleaseAsync:
                            the movement's creator counts as the receiving manager's assent, so
                            designating yourself raises no acceptance step. Promising one here would
                            be the same silent gap this field exists to close, just reversed. */}
                        {initiatorIsHr
                          ? t('Movements initiated by HR are approved on creation, so no manager is asked to approve.')
                          : !formData.managerId
                            ? t('This movement will skip receiving-manager approval and go straight to HR.')
                            : formData.managerId === currentUserId
                              ? t('You are the receiving manager, so no acceptance step is added and this movement goes to HR.')
                              : t('{{name}} must accept this movement before it reaches HR.', { name: selectedManagerName })}
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Reason for Movement')}
            </label>
            <textarea
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              rows={3}
              placeholder={t('Provide justification for this movement...')}
              className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              disabled={!formData.userId || !formData.movementTypeId || !formData.effectiveDate || submitting}
              className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
            >
              {submitting && <Loader className="w-4 h-4 mr-2 animate-spin" />}
              {t('Create Movement')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InitiateMovementModal;
